import React, { useEffect } from 'react'
import { logSessionInfo } from '../../../../../services/logger'
import './AudioIssueInterstitialView.scss'

/**
 * Interstitial screen shown when an audio issue is detected mid-assessment.
 * Displays different content based on the type of audio issue:
 * - isClipped: Student may be too close to mic or speaking too loudly. Shows "Suggestions" button.
 * - deviceIssue: Possible microphone, headset, or device problem. Shows "Test Microphone" button.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.audioIssueType - Object with isClipped and deviceIssue booleans.
 * @param {Function} props.goToStudentUseIssue - Navigates to StudentUseIssueView (for clipped audio).
 * @param {Function} props.goToCheckHeadset - Navigates to CheckHeadsetView (for device issues).
 * @param {Function} props.goToListenToRecording - Navigates to ListenToRecordingView.
 * @param {Function} props.goToRetry - Navigates back to RetryView (exit troubleshoot).
 * @param {Function} props.goToAudioIssueInterstitial - Navigates back to this interstitial view.
 */
export default function AudioIssueInterstitialView(props) {
  const { audioIssueType = {}, goToStudentUseIssue, goToCheckHeadset, goToListenToRecording, goToRetry, goToAudioIssueInterstitial } = props

  const isClipped = audioIssueType.isClipped
  const isDeviceIssue = audioIssueType.deviceIssue

  const subtitle = isClipped
    ? 'Student may be too close to the microphone or speaking too loudly.'
    : 'There may be a problem with the microphone, headset, or device.'

  const handlePrimaryClick = () => {
    if (isClipped) {
      logSessionInfo('teacherTroubleshoot_AUDIO_ISSUE_SUGGESTIONS_CLICKED', { audioIssueType })
      // Navigate to StudentUseIssueView with back button routing to this interstitial
      goToStudentUseIssue(goToAudioIssueInterstitial)
    } else if (isDeviceIssue) {
      logSessionInfo('teacherTroubleshoot_AUDIO_ISSUE_TEST_MIC_CLICKED', { audioIssueType })
      // Navigate to CheckHeadsetView with back button routing to this interstitial
      goToCheckHeadset(goToAudioIssueInterstitial)
    }
  }

  const handleListenToRecording = () => {
    logSessionInfo('teacherTroubleshoot_AUDIO_ISSUE_LISTEN_CLICKED', { audioIssueType })
    goToListenToRecording()
  }

  const handleExitTroubleshoot = () => {
    logSessionInfo('teacherTroubleshoot_AUDIO_ISSUE_EXIT_CLICKED', { audioIssueType })
    goToRetry()
  }

  return (
    <div className="audio-issue-interstitial-view">
      <div className="audio-issue-interstitial-view__content">
        <div className="audio-issue-interstitial-view__title">
          Audio quality issues have been detected in this session.
        </div>
        <div className="audio-issue-interstitial-view__subtitle">
          {subtitle}
        </div>

        <div className="audio-issue-interstitial-view__buttons">
          <button
            className="audio-issue-interstitial-view__button audio-issue-interstitial-view__button--primary"
            onClick={handlePrimaryClick}
          >
            {isClipped ? 'Suggestions' : 'Test Microphone'}
          </button>
          <button
            className="audio-issue-interstitial-view__button audio-issue-interstitial-view__button--secondary"
            onClick={handleListenToRecording}
          >
            Listen to Student Audio
          </button>
          <button
            className="audio-issue-interstitial-view__button audio-issue-interstitial-view__button--secondary"
            onClick={handleExitTroubleshoot}
          >
            Exit Troubleshoot
          </button>
        </div>
      </div>
    </div>
  )
}
